/**
 * 多语言国际化模块 (i18n)
 * 支持动态语言切换，用户选择保存在 localStorage
 */

import zhCN from './zh-CN';
import enUS from './en-US';

const STORAGE_KEY = 'pidoc_language';
const DEFAULT_LANG = 'zh-CN';

export type SupportedLang = 'zh-CN' | 'en-US';
export type TranslationTree = Record<string, unknown>;

const bundles: Record<SupportedLang, TranslationTree> = {
  'zh-CN': zhCN as TranslationTree,
  'en-US': enUS as TranslationTree,
};

const supportedLanguages: Record<SupportedLang, string> = {
  'zh-CN': '中文',
  'en-US': 'English',
};

let currentLanguage: SupportedLang = DEFAULT_LANG;
let translations: TranslationTree = bundles[DEFAULT_LANG];

function detectLanguage(): SupportedLang {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved && saved in supportedLanguages) return saved as SupportedLang;
  const browserLang =
    navigator.language || (navigator as unknown as { userLanguage?: string }).userLanguage;
  if (browserLang?.startsWith('zh')) return 'zh-CN';
  return 'en-US';
}

export function init(): void {
  currentLanguage = detectLanguage();
  translations = bundles[currentLanguage];
}

export function setLanguage(lang: string): boolean {
  if (!(lang in supportedLanguages)) {
    console.error(`Unsupported language: ${lang}`);
    return false;
  }
  currentLanguage = lang as SupportedLang;
  translations = bundles[currentLanguage];
  localStorage.setItem(STORAGE_KEY, lang);
  window.dispatchEvent(new CustomEvent('languageChange', { detail: { language: lang } }));
  return true;
}

export function getLanguage(): SupportedLang {
  return currentLanguage;
}

export function getSupportedLanguages(): Record<SupportedLang, string> {
  return supportedLanguages;
}

export function t(key: string, params?: Record<string, unknown> | null): string {
  const keys = key.split('.');
  let value: unknown = translations;
  for (const k of keys) {
    if (value && typeof value === 'object' && k in (value as TranslationTree)) {
      value = (value as TranslationTree)[k];
    } else {
      console.warn(`Translation not found: ${key}`);
      return key;
    }
  }
  if (typeof value !== 'string') return key;

  if (params && typeof params === 'object') {
    return value.replace(/\{\{(\w+)\}\}/g, (match, paramKey: string) => {
      const v = params[paramKey];
      return v !== undefined ? String(v) : match;
    });
  }
  return value;
}

export function translatePage(): void {
  document.querySelectorAll<HTMLElement>('[data-i18n]').forEach((el) => {
    const k = el.getAttribute('data-i18n');
    if (k) el.textContent = t(k);
  });
  document.querySelectorAll<HTMLElement>('[data-i18n-placeholder]').forEach((el) => {
    const k = el.getAttribute('data-i18n-placeholder');
    if (k && 'placeholder' in el) (el as HTMLInputElement).placeholder = t(k);
  });
  document.querySelectorAll<HTMLElement>('[data-i18n-title]').forEach((el) => {
    const k = el.getAttribute('data-i18n-title');
    if (k) el.title = t(k);
  });
}

export const I18nModule = {
  init,
  setLanguage,
  getLanguage,
  getSupportedLanguages,
  t,
  translatePage,
};

// 初始化一次
init();

// 为避免循环依赖（api/utils 会引用 i18n），提供一个懒获取函数
export function getI18n(): typeof I18nModule | null {
  return I18nModule;
}

// 兼容层
declare global {
  interface Window {
    i18nModule?: typeof I18nModule;
    PidocTranslations?: Record<string, TranslationTree>;
  }
}
window.i18nModule = I18nModule;
window.PidocTranslations = bundles;
